import {
    faAddressCard,
    faBriefcase,
    faCalendarAlt,
    faDesktop,
    faHospital,
    faSitemap,
    faTaxi,
} from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { ConfigConstants } from '../../../config/config-constants';
import { ODataObject } from '../../../interfaces/odata-model.interface';
import { ConfigService } from '../../../services/config/config.service';
import { CustomInjector } from '../../../services/custom-injector/custom-injector';
import { InternalInfoUtilities } from '../../../utilities/internal-info-utilities';
import { ODataLTextModel } from '../../../utilities/text-input-utilities';
import { CompositeModel } from '../../complex/composite.model';
import { ODataInfoTextLineOutModel } from '../../primitive/internal-info-multiline-text.model';
import { CategoryModel } from './category.model';
import TEXT_ID = ConfigConstants.INFO_TEXTID;
import INTERNAL_INFO_CATEGORY = ConfigConstants.INTERNAL_INFO_CATEGORY;
import { DateUtilities } from '../../../utilities/date-utilities';
import { assertUnreachable } from '../../../utilities/assert-unreachable';

const ODataParking = INTERNAL_INFO_CATEGORY.PARKING;
const ODataTechnicalSpec = INTERNAL_INFO_CATEGORY.TECHNICAL_SPEC;
const ODataOrganisationMatter = INTERNAL_INFO_CATEGORY.ORG_MATTERS;
const ODataConstancyCheck = INTERNAL_INFO_CATEGORY.CONSTANCY_CHECK;
const ODataDispatch = INTERNAL_INFO_CATEGORY.DISPATCH;
const ODataSubSystem = INTERNAL_INFO_CATEGORY.SUB_SYSTEMS;

export class InternalInfoCategoryModel extends CompositeModel {
    public contact: CategoryModel;
    public parking: CategoryModel;
    public technicalSpec: CategoryModel;
    public organisation: CategoryModel;
    public constancyCheck: CategoryModel;
    public dispatch: CategoryModel;
    public subSystem: CategoryModel;

    public categoryArray: CategoryModel[] = [];

    public gid: string;
    public configService: ConfigService;
    public constructor(
        parking: string,
        techSpecification: string,
        orgMatter: string,
        constancyCheck: string,
        dispatch: string,
        subSystem: string,
        functionalLoc: string,
    ) {
        super('Category');
        const injector = CustomInjector.getInjector();
        const configService = injector.get(ConfigService);
        const translate = injector.get(TranslateService);
        this.gid = configService.getUserFrontId();

        this.contact = new CategoryModel(
            '',
            '',
            '',
            '',
            faAddressCard,
            <string>translate.instant('INTERNAL_INFO_CONTACT_CATEGORY'),
            INTERNAL_INFO_CATEGORY.CONTACT,
        );
        this.parking = new CategoryModel(
            ODataParking,
            parking,
            functionalLoc,
            TEXT_ID.PARKING,
            faTaxi,
            <string>translate.instant('INTERNAL_INFO_PARKING_CATEGORY'),
            INTERNAL_INFO_CATEGORY.PARKING,
        );
        this.technicalSpec = new CategoryModel(
            ODataTechnicalSpec,
            techSpecification,
            functionalLoc,
            TEXT_ID.TECHNICAL_SPEC,
            faBriefcase,
            <string>translate.instant('INTERNAL_INFO_TECH_SPEC_CATEGORY'),
            INTERNAL_INFO_CATEGORY.TECHNICAL_SPEC,
        );
        this.organisation = new CategoryModel(
            ODataOrganisationMatter,
            orgMatter,
            functionalLoc,
            TEXT_ID.ORG_MATTER,
            faCalendarAlt,
            <string>translate.instant('INTERNAL_INFO_ORGANIZATION_MATTER_CATEGORY'),
            INTERNAL_INFO_CATEGORY.ORG_MATTERS,
        );
        this.constancyCheck = new CategoryModel(
            ODataConstancyCheck,
            constancyCheck,
            functionalLoc,
            TEXT_ID.CONSTANCY,
            faHospital,
            <string>translate.instant('INTERNAL_INFO_CONSTANCY_CHECK_CATEGORY'),
            INTERNAL_INFO_CATEGORY.CONSTANCY_CHECK,
        );
        this.dispatch = new CategoryModel(
            ODataDispatch,
            dispatch,
            functionalLoc,
            TEXT_ID.DISPATCH,
            faDesktop,
            <string>translate.instant('INTERNAL_INFO_DISPATCH_CATEGORY'),
            INTERNAL_INFO_CATEGORY.DISPATCH,
        );
        this.subSystem = new CategoryModel(
            ODataSubSystem,
            subSystem,
            functionalLoc,
            TEXT_ID.SUBSYSTEM,
            faSitemap,
            <string>translate.instant('INTERNAL_INFO_SUB_SYSTEM_CATEGORY'),
            INTERNAL_INFO_CATEGORY.SUB_SYSTEMS,
        );

        this.getAllCategoriesModel();
    }

    public getAllCategoriesModel(): CategoryModel[] {
        this.categoryArray = [];

        this.categoryArray.push(
            this.contact,
            this.parking,
            this.technicalSpec,
            this.organisation,
            this.constancyCheck,
            this.dispatch,
            this.subSystem,
        );
        return this.categoryArray;
    }

    public getSelectedCategory(selectedCategory: INTERNAL_INFO_CATEGORY): CategoryModel {
        switch (selectedCategory) {
            case INTERNAL_INFO_CATEGORY.PARKING:
                return this.parking;
            case INTERNAL_INFO_CATEGORY.ORG_MATTERS:
                return this.organisation;
            case INTERNAL_INFO_CATEGORY.TECHNICAL_SPEC:
                return this.technicalSpec;
            case INTERNAL_INFO_CATEGORY.CONSTANCY_CHECK:
                return this.constancyCheck;
            case INTERNAL_INFO_CATEGORY.DISPATCH:
                return this.dispatch;
            case INTERNAL_INFO_CATEGORY.SUB_SYSTEMS:
                return this.subSystem;
            case INTERNAL_INFO_CATEGORY.CONTACT:
                return this.contact;
            default:
                assertUnreachable(selectedCategory);
        }
    }

    public updateHeaderInfo(category: CategoryModel): void {
        const date = DateUtilities.formatDate(moment());

        if (!category.isHeaderInfoAlreadyPresent()) {
            category.headerInfo = `${date} by ${this.gid}`;
            // we should always append HeaderInfo in the new line.
            // If TextLine is empty or textLine already have '\n' then no need to append header info in next line
            category.textLine.value = category.textLine.value.replace(/^\s+|\s+$/g, '');

            if (!category.textLine.value) {
                category.textLine.value = category.headerInfo;
            } else if (category.textLine.value.endsWith('\n')) {
                category.textLine.value += category.headerInfo;
            } else {
                category.textLine.value += `\n${category.headerInfo}`;
            }
        }
    }

    public static buildCategoryModel(response: ODataLTextModel[], functionalLoc: string): InternalInfoCategoryModel {
        const parking = InternalInfoUtilities.getInfoLongTextById(response, TEXT_ID.PARKING);
        const techSpecification = InternalInfoUtilities.getInfoLongTextById(response, TEXT_ID.TECHNICAL_SPEC);
        const orgMatter = InternalInfoUtilities.getInfoLongTextById(response, TEXT_ID.ORG_MATTER);
        const constancyCheck = InternalInfoUtilities.getInfoLongTextById(response, TEXT_ID.CONSTANCY);
        const dispatch = InternalInfoUtilities.getInfoLongTextById(response, TEXT_ID.DISPATCH);
        const subSystem = InternalInfoUtilities.getInfoLongTextById(response, TEXT_ID.SUBSYSTEM);

        return new InternalInfoCategoryModel(
            parking,
            techSpecification,
            orgMatter,
            constancyCheck,
            dispatch,
            subSystem,
            functionalLoc,
        );
    }
}

export interface ODataInternalInfoCategoryModel extends ODataObject {
    Parking: ODataInfoTextLineOutModel[];
    Contacts: ODataInfoTextLineOutModel[];
    TechnicalSpecifics: ODataInfoTextLineOutModel[];
    OrganizationalMatters: ODataInfoTextLineOutModel[];
    ConstancyCheck: ODataInfoTextLineOutModel[];
    Dispatch: ODataInfoTextLineOutModel[];
    SubSystems: ODataInfoTextLineOutModel[];
}
